import { UNTRANSLATED_LOCALE } from '@va/constants';
import { storageItems } from '@va/standalone/shared/constants';
import { extractLanguage } from '@va/util/helpers';
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import 'dayjs/locale/de';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/it';
import 'dayjs/locale/pt';
import 'dayjs/locale/ro';
import { MDText } from 'i18n-react';
import moment from 'moment';
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { handleCustomTags } from '../helpers/helpers';

dayjs.extend(calendar);

export const LocalizationContext = createContext({
  locale: 'en',
  setLocale: (locale: string) => Promise.resolve(),
  translate: (_key: string, _options?: Record<string, unknown>) => '' as any,
  rawTranslate: (_key: string, _options?: Record<string, unknown>) => '' as any,
  isLoading: false,
  translationsError: false,
});

export const useLocalizationContext = () => useContext(LocalizationContext);

type TranslationsProviderProps = {
  fetchTranslations: (locale: string) => Promise<Record<string, unknown>>;
  onLocaleChange?: (l: string) => void;
  updateURL?: (newLocale: string) => void;
  useDefaultOptions?: () => Record<string, any>;
};

export const TranslationsProvider = ({
  children,
  fetchTranslations,
  onLocaleChange,
  useDefaultOptions,
  updateURL,
}: PropsWithChildren<TranslationsProviderProps>) => {
  const [{ locale, values }, setLocale] = useState({
    locale: extractLanguage(),
    values: {},
  });
  const translationsRequestFinished = useRef(false);
  const translationsRequestError = useRef(false);
  const defaultOptions = useDefaultOptions?.();

  const updateLocale = useCallback(
    async (newLocale: string) => {
      if (translationsRequestFinished.current && newLocale === locale) return;
      updateURL?.(newLocale);
      localStorage.setItem(storageItems.locale, newLocale);

      if (newLocale === UNTRANSLATED_LOCALE) {
        translationsRequestFinished.current = true;
        setLocale({
          locale: newLocale,
          values: {},
        });
        return;
      }
      try {
        const newValues = await fetchTranslations(newLocale);
        moment.locale(newLocale);
        dayjs.locale(newLocale);

        setLocale({
          locale: newLocale,
          values: newValues,
        });
        onLocaleChange?.(newLocale);
      } catch {
        translationsRequestError.current = true;
        setLocale((p) => ({
          locale: p.locale,
          values: {},
        }));
      } finally {
        translationsRequestFinished.current = true;
      }
    },
    [locale, fetchTranslations, onLocaleChange],
  );

  const translator = useMemo(() => {
    return new MDText(values);
  }, [values]);

  /**
   * Returns the raw value received from Tolgee, without passing it through the markdown parser
   */
  const rawTranslateCallback = useCallback(
    (key: string) => {
      const result = key.split('.').reduce((obj: any, part) => obj?.[part], values);
      return typeof result === 'string' ? result : key;
    },
    [values],
  );

  const translateCallback = useCallback(
    (key: string, options?: Record<string, any>) => {
      const translatedText = translator.translate(key, { ...defaultOptions, ...options });
      if (locale === UNTRANSLATED_LOCALE) {
        return key;
      }
      if (translatedText && typeof translatedText === 'object') {
        return (
          <>
            {(translatedText as Array<unknown>).map((item) => {
              if (typeof item === 'string') return handleCustomTags(item);
              return item;
            })}
          </>
        );
      }

      return translatedText ? handleCustomTags(translatedText as string) : translatedText;
    },
    [defaultOptions, locale, translator],
  );

  useEffect(() => {
    updateLocale(locale);
  }, [locale, updateLocale]);

  const context = {
    locale,
    setLocale: updateLocale,
    translate: translateCallback,
    rawTranslate: rawTranslateCallback,
    isLoading: !translationsRequestFinished.current,
    translationsError: translationsRequestError.current,
  };

  return <LocalizationContext.Provider value={context}>{children}</LocalizationContext.Provider>;
};
